import { marketingAdminAuthHeader, marketingGenerateBlogUrl } from './marketingAdminService';

let clientStopRequested = false;

async function parseJsonResponse(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    throw new Error(
      res.ok
        ? 'Invalid response from blog API'
        : text.slice(0, 120) || `Blog API error (${res.status})`,
    );
  }
}

/** Starts batch generation for all pending keywords (runs in background on marketing-site). */
export async function startBlogBatchGeneration() {
  const res = await fetch(marketingGenerateBlogUrl(), {
    method: 'POST',
    headers: await marketingAdminAuthHeader(),
  });

  const data = await parseJsonResponse(res);

  if (res.status === 401) {
    throw new Error(data.error || 'Unauthorized');
  }

  return { ok: res.ok, status: res.status, data };
}

/** Poll batch progress while marketing-site generates blogs. */
export async function fetchBlogBatchStatus() {
  const res = await fetch(marketingGenerateBlogUrl(), {
    method: 'GET',
    headers: await marketingAdminAuthHeader(),
  });

  const data = await parseJsonResponse(res);

  if (!res.ok) {
    throw new Error(data.error || `Blog API error (${res.status})`);
  }

  return data;
}

/** Ask marketing-site to stop the running batch (after current keyword). */
export async function stopBlogBatchGeneration() {
  clientStopRequested = true;
  const res = await fetch(marketingGenerateBlogUrl(), {
    method: 'DELETE',
    headers: await marketingAdminAuthHeader(),
  });

  const data = await parseJsonResponse(res);

  if (res.status === 401) {
    throw new Error(data.error || 'Unauthorized');
  }

  return { ok: res.ok, status: res.status, data };
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Start batch + poll until finished or stopped. Calls onProgress with status updates. */
export async function runBlogBatchGeneration(onProgress) {
  clientStopRequested = false;
  const start = await startBlogBatchGeneration();

  if (!start.data.started && !start.data.alreadyRunning) {
    if (start.data.message) {
      return {
        finished: true,
        ...start.data,
        succeeded: 0,
        failed: 0,
        total: 0,
        completed: 0,
        running: false,
      };
    }
    throw new Error(start.data.error || start.data.message || 'Could not start batch');
  }

  let status = await fetchBlogBatchStatus();
  if (onProgress) onProgress(status);

  while (status.running && !clientStopRequested) {
    await sleep(4000);
    status = await fetchBlogBatchStatus();
    if (onProgress) onProgress(status);
  }

  if (clientStopRequested && status.running) {
    status = await fetchBlogBatchStatus();
    if (onProgress) onProgress(status);
  }

  return {
    finished: true,
    stopped: clientStopRequested || Boolean(status.finishedAt && !status.running),
    ...status,
  };
}

/** @deprecated Use runBlogBatchGeneration — kept for scripts. */
export async function runBlogGeneration() {
  const result = await runBlogBatchGeneration();
  if (result.succeeded === 0 && result.failed === 0 && result.message) {
    return { success: false, message: result.message };
  }
  return {
    success: (result.succeeded || 0) > 0,
    message: `Batch done: ${result.succeeded || 0} created, ${result.failed || 0} failed.`,
  };
}
